/* eslint-disable @typescript-eslint/no-explicit-any */
import type { DownloadOptions, SortOptions } from "@/constants";
import {
  DEFAULT_DISTANCE,
  DEFAULT_LIMIT,
  DEFAULT_SORT,
  DEFAULT_TIER,
} from "@/constants";
import type Procedure from "@/model/procedure";
import type Place from "@/model/place";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type Speciality from "@/model/speciality";
import type Pos from "@/model/pos";
import type { networkConfigItemType } from "@/model/network";
import type Network from "@/model/network";
import type Distance from "@/model/distance";
import type Facility from "@/model/facility";
import type Provider from "@/model/provider";
import type { ICostEstFilterParams } from "@/hooks/use-cost-est-filter";
import { getNetworksFromTiers } from "@/lib/cost-estimator-utils";

const initialState: ICostEstFilterParams = {
  key: "",
  procedure: null as Procedure | null,
  targetLocation: null as Place | null,
  distance: DEFAULT_DISTANCE,
  pos: [] as Pos[],
  specialities: [] as Speciality[],
  tiers: [] as { name: string }[],
  networks: [] as Network[],
  facility: null as Facility | null,
  provider: null as Provider | null,
  sort: DEFAULT_SORT,
  limit: DEFAULT_LIMIT,
  transformedNetworkTier: null,
};

const priceTransparencySlice = createSlice({
  name: "priceTransparency",
  initialState,
  reducers: {
    setKey(state: any, action: PayloadAction<string>) {
      state.key = action.payload;
    },
    setProcedure(state: any, action: PayloadAction<Procedure | null>) {
      state.procedure = action.payload;
    },
    setLocation(state: any, action: PayloadAction<Place | null>) {
      state.targetLocation = action.payload;
    },
    setDistance(state: any, action: PayloadAction<Distance>) {
      state.distance = action.payload;
    },

    setTiers(state: any, action: PayloadAction<{ name: string }[]>) {
      state.tiers = action.payload;
    },
    setNetworks(state: any, action: PayloadAction<Network[]>) {
      state.networks = action.payload;
    },
    setFacility(state: any, action: PayloadAction<Facility | null>) {
      state.facility = action.payload;
    },
    setProvider(state: any, action: PayloadAction<Provider | null>) {
      state.provider = action.payload;
    },

    setSpecialities(state: any, action: PayloadAction<Speciality[]>) {
      state.specialities = action.payload;
    },

    setPos(state: any, action: PayloadAction<Pos[]>) {
      state.pos = action.payload;
    },

    setSort(state: any, action: PayloadAction<SortOptions>) {
      state.sort = action.payload;
    },

    setLimit(state: any, action: PayloadAction<DownloadOptions>) {
      state.limit = action.payload;
    },
    setTransformedNetworkTier: (
      state,
      action: PayloadAction<Record<string, networkConfigItemType>>,
    ) => {
      state.transformedNetworkTier = action.payload;
    },
    clearCostEstimatorFilter(state: any, action: PayloadAction<Speciality[]>) {
      const tiers = Object.values(
        state.transformedNetworkTier as Record<string, networkConfigItemType>,
      ).map((item) => item.tier);
      const defaultTier =
        tiers.length === 1
          ? tiers[0]
          : tiers.filter((tier) => tier === DEFAULT_TIER[0].name)[0];
      const DEFAULT_NETWORKS = getNetworksFromTiers(
        [{ name: defaultTier }],
        state.transformedNetworkTier,
      );
      state.networks = DEFAULT_NETWORKS!;
      state.tiers = [{ name: defaultTier }];
      state.distance = DEFAULT_DISTANCE;
      state.sort = DEFAULT_SORT;
      state.limit = DEFAULT_LIMIT;
      state.pos = [];
      state.facility = null;
      state.provider = null;
      state.specialities = action.payload;
    },

    resetcostEstimatorFilter(state: any, action: PayloadAction<Place | null>) {
      state.procedure = null;
      state.targetLocation = action.payload;
      state.distance = DEFAULT_DISTANCE;
      state.sort = DEFAULT_SORT;
      state.limit = DEFAULT_LIMIT;
      state.pos = [];
      state.specialities = [];
      // state.tiers = [];
      // state.networks = [];
      state.facility = null;
      state.provider = null;
    },
  },
});

export const {
  setKey,
  setProcedure,
  setLocation,
  setDistance,
  setTiers,
  setNetworks,
  setFacility,
  setProvider,
  setSpecialities,
  setPos,
  setSort,
  setLimit,
  setTransformedNetworkTier,
  clearCostEstimatorFilter,
  resetcostEstimatorFilter,
} = priceTransparencySlice.actions;

export default priceTransparencySlice.reducer;
